import React, { useEffect } from 'react';
import Grid from '../Grid/Grid';
import Map from '../Map/Map';
import { useAppDispatchActions, useAppState } from '../../context/AppStateProvider/useAppState';
import useSWR from 'swr';
import { fetcher } from '../../utils/wordpress';
import type {
    Event,
    EventSessionFilters,
    Itinerary,
    User,
    AmenityType,
    Category,
} from '../../context/AppStateProvider/types';
import Filters from '../Filters/Filters';
import Registration, { RegisterCard } from '../Registration/Registration';
import { ApiProvider } from '../../context/ApiProvider/ApiProvider';
import type { PointOfInterestResponse } from '../../context/AppStateProvider/types';
import Parking from '../Parking/Parking';

const Main: React.FC = () => {
    const {
        setEvents,
        setActivityTypes,
        setInterests,
        setFilteredEvents,
        setUser,
        setItinerary,
        setViewMode,
        setActiveEvent,
        setAmenityTypes,
        setAmenities,
        setIsRegistrationOpen,
    } = useAppDispatchActions();
    const { state } = useAppState();
    const {
        selectedActivityTypes,
        selectedInterests,
        viewMode,
        itinerary,
        mapExpanded,
        splitRatio,
        interests,
        events,
        user,
        isRegistrationOpen,
        activeEvent,
    } = state;

    const [headerHeight, setHeaderHeight] = React.useState(0);

    useEffect(() => {
        const updateHeaderHeight = () => {
            const header = document.querySelector('header.header');
            if (header) {
                setHeaderHeight(header.getBoundingClientRect().height);
            }
        };

        updateHeaderHeight();
        window.addEventListener('resize', updateHeaderHeight);

        return () => window.removeEventListener('resize', updateHeaderHeight);
    }, []);

    const {
        data: eventData,
        isLoading: eventLoading,
        mutate: eventMutate,
    } = useSWR<Event[]>('/wp-json/open-day-26/v1/event-sessions', fetcher, {
        revalidateOnFocus: false,
        refreshInterval: 900000,
    });

    const {
        data: filterData,
        isLoading: filterLoading,
        mutate: filterMutate,
    } = useSWR<EventSessionFilters>('/wp-json/open-day-26/v1/event-session-filters', fetcher, {
        revalidateOnFocus: false,
    });

    const {
        data: itineraryData,
        error: itineraryError,
        isLoading: itineraryLoading,
        mutate: itineraryMutate,
    } = useSWR<Itinerary | null>(user ? '/wp-json/open-day-26/v1/itinerary' : null, fetcher, {
        revalidateOnFocus: false,
    });

    const { data: amenityTypeData } = useSWR<AmenityType[]>(
        '/wp-json/open-day-26/v1/point-of-interest-types',
        fetcher,
        {
            revalidateOnFocus: false,
        },
    );

    const {
        data: userData,
        error: userError,
        isLoading: userLoading,
        mutate: userMutate,
    } = useSWR<User | null>('/wp-json/open-day-26/v1/account', fetcher, {
        revalidateOnFocus: false,
        shouldRetryOnError: false,
    });

    const { data: amenityData } = useSWR<PointOfInterestResponse>(
        '/wp-json/open-day-26/v1/points-of-interest',
        fetcher,
        {
            revalidateOnFocus: false,
        },
    );

    useEffect(() => {
        if (eventData && Array.isArray(eventData) && interests) {
            const updatedEvents = eventData.map((event) => {
                let category: Category | null = null;

                // 1. Check if primary_area_of_interest is set
                if (event.primary_area_of_interest) {
                    category = event.area_of_interest.find((c) => c.id === event.primary_area_of_interest) || null;
                }

                // 2. If no primary or not found, fall back to existing logic (first non-gold)
                if (!category) {
                    const firstNonGoldCategory = event.area_of_interest.find((c) => {
                        const interest = interests.find((interest) => interest.slug === c?.slug);
                        return interest && interest.colour !== 'gold';
                    });
                    category =
                        (firstNonGoldCategory ?? event.area_of_interest.length) ? event.area_of_interest[0] : null;
                }

                const interest = interests.find((interest) => interest.slug === category?.slug);

                const colours = {
                    gold: '#CC9900',
                    orange: '#FF6600',
                    blue: '#00B0F0',
                    green: '#4EA72E',
                    pink: '#FF99FF',
                    purple: '#D86DCB',
                };

                type ColourKey = keyof typeof colours;

                return {
                    ...event,
                    colour: interest?.colour && interest.colour in colours ? interest.colour : 'gold',
                    marker_colour:
                        interest?.colour && interest.colour in colours
                            ? colours[interest.colour as ColourKey]
                            : colours.gold,
                };
            });

            setEvents(updatedEvents);
        }
    }, [eventData, interests, setEvents]);

    useEffect(() => {
        if (userError) {
            setUser(null);
            return;
        }

        setUser(userData ?? null);
    }, [userData, setUser, userError]);

    useEffect(() => {
        if (itineraryError) {
            setItinerary(null);
            return;
        }

        setItinerary(itineraryData ?? null);
    }, [itineraryData, setItinerary, itineraryError]);

    useEffect(() => {
        if (filterData?.areas_of_interest && Array.isArray(filterData.areas_of_interest)) {
            const sortedInterests = [...filterData.areas_of_interest].sort((a, b) => a.name.localeCompare(b.name));
            setInterests(sortedInterests);
        }

        if (filterData?.session_types && Array.isArray(filterData.session_types)) {
            const sortedActivityTypes = [...filterData.session_types].sort((a, b) => a.name.localeCompare(b.name));
            setActivityTypes(sortedActivityTypes);
        }

        if (amenityTypeData && Array.isArray(amenityTypeData)) {
            let cancelled = false;
            (async () => {
                const processed = await Promise.all(
                    amenityTypeData.map(async (type) => {
                        const parsedPoi = (type.poi as unknown as string[]).map((p) => parseInt(p.split(' - ')[0], 10));
                        let iconId = type.icon_id || null;

                        if (!iconId && parsedPoi.length > 0) {
                            try {
                                const response = await fetch(`https://api.mazemap.com/api/types/${parsedPoi[0]}`);
                                const data = await response.json();
                                iconId = data.icon_id;
                            } catch (error) {
                                console.error('Error fetching icon_id for POI', parsedPoi[0], error);
                            }
                        }

                        return {
                            ...type,
                            poi: parsedPoi,
                            icon_id: iconId,
                        };
                    }),
                );

                if (cancelled) return;
                const sortedAmenityTypes = processed.sort((a, b) => a.name.localeCompare(b.name));
                setAmenityTypes(sortedAmenityTypes);
            })();

            return () => {
                cancelled = true;
            };
        }
    }, [filterData, setActivityTypes, setInterests, amenityTypeData, setAmenityTypes]);

    useEffect(() => {
        if (amenityData?.features) {
            const mappedAmenities = amenityData.features.map((feature) => ({
                id: feature.properties.id,
                title: feature.properties.title,
                type: feature.properties.type,
                is_parking: feature.properties.is_parking,
                location_name: feature.properties.location_name,
                content: feature.properties.content,
                image: feature.properties.image,
                location_data: {
                    v: '1',
                    config: '',
                    zlevel: '1',
                    center: feature.geometry.coordinates,
                    zoom: '18',
                    sharepoitype: 'poi',
                    sharepoi: feature.properties.id.toString(),
                    campusid: '296',
                    utm_medium: 'map',
                },
            }));
            setAmenities(mappedAmenities);
        }
    }, [amenityData, setAmenities]);

    useEffect(() => {
        if (events && Array.isArray(events)) {
            let filtered: Event[] = [];

            if (viewMode === 'itinerary') {
                // Filter events that are in the itinerary
                if (itinerary && itinerary.sessions) {
                    filtered = events.filter((event) => itinerary.sessions.includes(event.id));
                }
            } else {
                // Default 'events' view filtering
                filtered = events.filter((event) => {
                    const activityMatch =
                        selectedActivityTypes.length === 0 ||
                        (event.session_type &&
                            event.session_type.some((type) => selectedActivityTypes.includes(type.slug)));
                    const interestMatch =
                        selectedInterests.length === 0 ||
                        (event.area_of_interest &&
                            event.area_of_interest.some((interest) => selectedInterests.includes(interest.slug)));
                    return activityMatch && interestMatch;
                });
            }
            setActiveEvent(null);
            setFilteredEvents(filtered);
        }
    }, [events, setFilteredEvents, selectedActivityTypes, selectedInterests, viewMode, itinerary, setActiveEvent]);

    useEffect(() => {
        // --- GTM TRACKING START ---
        (window as any).dataCoreLayer = (window as any).dataCoreLayer || [];

        (window as any).dataCoreLayer.push({
            event: 'od_planner_tab_click',
            click_text: viewMode === 'events' ? 'Events' : 'Itinerary',
            click_class: viewMode === 'events' ? 'tab-events' : 'tab-itinerary',
        });
        // --- GTM TRACKING END ---
    }, [viewMode]);

    useEffect(() => {
        if (!activeEvent) return;

        // --- GTM TRACKING START ---
        (window as any).dataCoreLayer = (window as any).dataCoreLayer || [];
        (window as any).dataCoreLayer.push({
            event: 'od_planner_view_session',
            click_text: activeEvent.title,
            click_classes: activeEvent.area_of_interest.map((interest) => interest.slug).join(' '),
        });
        // --- GTM TRACKING END ---
    }, [activeEvent]);

    useEffect(() => {
        // Expose the function to the window object
        (window as any).OpenDayMap = {
            setIsRegistrationOpen: (option: 0 | 1 | 2) => setIsRegistrationOpen(option),
        };

        return () => {
            delete (window as any).OpenDayMap;
        };
    }, []);

    return (
        <ApiProvider
            value={{
                userMutate,
                userLoading,
                itineraryMutate,
                itineraryLoading,
                eventLoading,
                eventMutate,
                filterLoading,
                filterMutate,
            }}>
            <div className='max-w-[400px] px-5 pt-5 text-left text-[38px] leading-10 font-black text-white uppercase md:hidden'>
                FIND SESSIONS AND SEE WHAT'S ON
            </div>
            <div className='grid grid-cols-1 grid-rows-[4.6em_calc(100dvh-4.6em-24px-64px)] gap-[12px] bg-(--colour-charcoal-light) md:grid-rows-[7em_46em] md:gap-[24px]'>
                <div className='flex justify-center'>
                    <div className='grid w-full grid-cols-2 gap-4 md:grid-cols-5 md:px-5'>
                        {/* Left Title Section */}
                        <div className='col-span-2 hidden items-end md:flex'>
                            <div className='max-w-[400px] text-left text-[38px] leading-10 font-black text-white uppercase'>
                                FIND SESSIONS AND SEE WHAT'S ON
                            </div>
                            <div className='flex-1'></div>
                        </div>

                        {/* Right Tabs Section */}
                        <div
                            className='col-span-3 flex items-end justify-start'
                            style={{ scrollMarginTop: headerHeight > 0 ? `${headerHeight}px` : undefined }}
                            id='open-day-map_tabs'>
                            {/* Tab 1: What's On (Events) */}
                            <button
                                onClick={() => setViewMode('events')}
                                className={`flex h-20 flex-1 cursor-pointer items-center justify-center gap-2.5 py-1 transition-colors md:h-auto ${
                                    viewMode === 'events'
                                        ? 'border-b-8 border-[var(--colour-gold)]'
                                        : 'border-b-[3px] border-gray-300 hover:text-[var(--colour-gold)]'
                                }`}>
                                {/* Calendar Icon */}
                                <svg
                                    className={`h-6 w-6 ${viewMode === 'events' ? 'text-(--colour-gold)' : 'text-gray-300'}`}
                                    viewBox='0 0 24 24'
                                    fill='currentColor'
                                    xmlns='http://www.w3.org/2000/svg'>
                                    <path d='M19 4H18V2H16V4H8V2H6V4H5C3.89 4 3.01 4.9 3.01 6L3 20C3 21.1 3.89 22 5 22H19C20.1 22 21 21.1 21 20V6C21 4.9 20.1 4 19 4ZM19 20H5V10H19V20ZM19 8H5V6H19V8ZM9 14H7V12H9V14ZM13 14H11V12H13V14ZM17 14H15V12H17V14ZM9 18H7V16H9V18ZM13 18H11V16H13V18ZM17 18H15V16H17V18Z' />
                                </svg>
                                <span
                                    className={`text-[18px] leading-10 font-bold sm:text-[22px] ${
                                        viewMode === 'events'
                                            ? 'text-[var(--colour-gold)]'
                                            : 'text-gray-300 hover:text-[var(--colour-gold)]'
                                    }`}>
                                    What's on
                                </span>
                            </button>

                            {/* Tab 2: Saved Items (Itinerary) */}
                            <button
                                onClick={() => setViewMode('itinerary')}
                                className={`flex h-20 flex-1 cursor-pointer items-center justify-center gap-2.5 py-1 transition-colors md:h-auto ${
                                    viewMode === 'itinerary'
                                        ? 'border-b-8 border-[var(--colour-gold)]'
                                        : 'over:text-[var(--colour-gold)] border-b-[3px] border-gray-300'
                                }`}>
                                {/* Star Icon */}
                                <svg
                                    className={`h-6 w-6 ${
                                        viewMode === 'itinerary' ? 'text-gray-300' : 'text-gray-300' // Note: Original active icon was yellow-600, inactive was gray-300. Assuming you want active itinerary to look like active events:
                                    } ${viewMode === 'itinerary' ? 'text-yellow-600' : 'text-gray-300'}`}
                                    viewBox='0 0 24 24'
                                    fill='currentColor'
                                    xmlns='http://www.w3.org/2000/svg'>
                                    <path d='M12 17.27L18.18 21L16.54 13.97L22 9.24L14.81 8.62L12 2L9.19 8.62L2 9.24L7.45 13.97L5.82 21L12 17.27Z' />
                                </svg>
                                <span
                                    className={`text-[18px] leading-10 font-bold sm:text-[22px] ${
                                        viewMode === 'itinerary'
                                            ? 'text-[var(--colour-gold)]'
                                            : 'text-gray-300 hover:text-[var(--colour-gold)]'
                                    }`}>
                                    Saved items
                                </span>
                                {itinerary && itinerary.sessions.length > 0 && (
                                    <div className='relative h-6 w-6 rounded-[30.55px] bg-[var(--colour-gold)] outline outline-[0.85px] outline-offset-[-0.85px] outline-[var(--colour-gold)]'>
                                        <div className='flex h-6 w-6 items-center justify-center rounded-full bg-[var(--colour-gold)] outline outline-[0.85px] outline-offset-[-0.85px] outline-[var(--colour-gold)]'>
                                            <span className="font-['Effra'] text-lg leading-none font-bold text-[var(--colour-charcoal-dark)]">
                                                {itinerary.sessions.length}
                                            </span>
                                        </div>
                                    </div>
                                )}
                            </button>
                        </div>
                    </div>
                </div>

                {/* Main Content */}
                <div
                    style={
                        {
                            '--split-ratio': `${splitRatio}%`,
                        } as React.CSSProperties
                    }
                    className={`relative grid h-full w-full grid-cols-1 grid-rows-[calc(var(--split-ratio))_calc(100%-var(--split-ratio))] rounded-[28px] bg-[var(--colour-grey-dark)] p-[15px] transition-[grid-template-columns,gap] duration-500 ease-in-out md:grid-rows-1 md:p-[20px] ${mapExpanded ? 'md:grid-cols-[0fr_1fr] md:gap-0' : 'md:grid-cols-[1fr_1fr] md:gap-[16px]'}`}>
                    {/* Left Column / Bottom Pane */}
                    <div className={`order-2 flex h-full min-w-0 flex-col gap-[16px] overflow-hidden md:order-1`}>
                        {viewMode === 'events' && <Filters className='hidden md:grid' />}
                        {viewMode === 'itinerary' && <Parking className='hidden md:inline-flex' />}
                        <Grid className='rounded-br-[16px] rounded-bl-[16px] border-r border-b border-l border-neutral-500 md:rounded-[16px] md:border-0' />
                    </div>
                    {/* Right Column / Top Pane */}
                    <div className='order-1 flex h-full flex-col gap-[16px] overflow-hidden md:order-2'>
                        {viewMode === 'itinerary' && <Parking className='inline-flex md:hidden' />}
                        {viewMode === 'events' && <Filters className='grid md:hidden' />}
                        <Map />
                    </div>
                    {/* Centered Modal Overlay */}
                    {viewMode === 'itinerary' && !user && !isRegistrationOpen && (
                        <div className='absolute inset-0 z-50 flex items-center justify-center rounded-[28px] bg-black/40 backdrop-blur-[2px]'>
                            <div className='rounded-[20px] bg-[var(--colour-white)] p-10'>
                                <RegisterCard />
                            </div>
                        </div>
                    )}
                </div>
            </div>
            <div className='mt-5 flex w-full justify-end md:hidden'>
                <a
                    href={
                        (window as any).openDayMapData?.map_url ||
                        'https://www.curtin.edu.au/open-day/wp-content/uploads/2026/02/6221OD_Open-Day_-A3-Map-2026_WEB.pdf'
                    }
                    target='_blank'
                    onClick={() => {
                        (window as any).dataCoreLayer = (window as any).dataCoreLayer || [];
                        (window as any).dataCoreLayer.push({
                            event: 'od_planner_button_click_download_map',
                            click_text: 'Download campus map pdf',
                            click_class: 'button-download-map',
                            value: 'Download campus map',
                        });
                    }}
                    className='inline-flex items-center justify-center gap-3 rounded-lg px-5 py-2 transition-opacity hover:opacity-80'>
                    <div className="font-['Effra'] text-base leading-5 font-normal text-white underline">
                        Download campus map pdf
                    </div>
                    <div className='flex items-center'>
                        <svg
                            width='17'
                            height='17'
                            viewBox='0 0 17 17'
                            fill='none'
                            xmlns='http://www.w3.org/2000/svg'>
                            <path
                                d='M7.96094 0.300781L16.125 8.05762L16.2373 8.16504L16.125 8.27148L7.96094 16.0176L7.8584 16.1143L7.75684 16.0166L6.9209 15.2139L6.80859 15.1064L6.9209 15L13.3809 8.86621H0.147461V7.44043H13.3799L6.9209 1.31836L6.80859 1.21191L6.9209 1.10449L7.75684 0.301758L7.8584 0.204102L7.96094 0.300781Z'
                                fill='white'
                                stroke='white'
                                strokeWidth='0.295078'
                            />
                        </svg>
                    </div>
                </a>
            </div>
            <Registration />
        </ApiProvider>
    );
};

export default Main;
